/**
 * ChatModePanel — text chat UI for the multi-channel widget.
 *
 * Plan reference: PR 7 — ChatModePanel + in-session switching.
 *
 * Renders a message list + composer that drives `useChatSession`. The
 * surrounding `AssistantPanelFrame` provides the close button and
 * positioning, so this component focuses on the in-panel chat surface.
 *
 * Codex hard rule #8 — voice ↔ chat NEVER concurrent. This component
 * does not start the voice session itself; switching back to voice is
 * delegated to a parent-supplied callback so the parent can tear chat
 * down BEFORE voice mounts.
 *
 * RTL is handled by `AssistantPanelFrame`'s `dir` prop on its wrapper;
 * inside the panel we use logical alignment via `marginInlineStart/End`
 * so Hebrew/Arabic message bubbles flip automatically.
 *
 * --- Scheduling flow (mirrors voice channel) ---
 *
 * When the server-side tool loop calls `book_meeting`, the dispatcher
 * (api/_lib/chat-tool-dispatcher.dispatchBookMeeting) does NOT actually
 * book — it returns { needs_contact_info: true, slotStart, … } and
 * tells the model to acknowledge briefly. We watch `lastToolEvents`
 * for that marker, then open `SchedulingContactPopup` to collect
 * name + email + phone. On submit we POST directly to /api/calendly-book
 * (with the resolved `customerId` from the chat hook), show the
 * Calendly iframe via `CalendlyPanel`, and inject a hidden system note
 * (`sendSystemNote`) so the bot acknowledges in the visitor's language.
 * A window postMessage listener for `calendly.event_scheduled` closes
 * the iframe and triggers a localized goodbye system note.
 *
 * When Calendly is OFF there is no calendar to chain into, so that path
 * keeps the inline flow instead: the model collects the lead in the chat
 * and it rides out to n8n with the transcript flush.
 *
 * This matches the voice channel's run-book-meeting.ts handler exactly,
 * just translated from datachannel sends to React state + hidden user
 * messages.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useChatSession, type ChatToolEvent } from '../chat-session/useChatSession';
import { postCalendlyBook } from '../api/voice-bot-client';
import type { ContactInfo, ScheduledMeeting, WidgetConfig } from '../types';
import type { CrossChannelMemory } from '../hooks/useCrossChannelMemory';
import {
  CALENDLY_SYSTEM_MESSAGES,
  DEFAULT_LANGUAGE,
  type LanguageCode,
} from '../config/language';
import { useIsMobile } from '../hooks/useIsMobile';
import { useCalendlyOverlaySync } from '../hooks/useCalendlyOverlaySync';
import { CalendlyPanel } from './CalendlyPanel';
import { SchedulingContactPopup } from './SchedulingContactPopup';
import { chatSounds } from '../utils/chat-sounds';
import { trackFacebookLead } from '../utils/facebook-pixel';
import { isTerminalGoodbye } from '../utils/terminal-goodbye';

interface ChatModePanelProps {
  /** Whether the panel is mounted by the parent shell. */
  isMounted: boolean;
  config: WidgetConfig;
  /** Whether voice is in this bot's enabledChannels. Controls the switch button. */
  voiceAvailable: boolean;
  /** Called when the user clicks "switch to voice". Parent must tear chat down. */
  onSwitchToVoice: () => void;
  onClose: () => void;
  /**
   * Shared conversation memory provided by the parent shell. When this
   * has prior messages (e.g. user just switched from voice), the chat
   * session seeds itself from this buffer and skips the greeting so the
   * bot continues mid-conversation transparently. Optional — when omitted
   * (legacy single-channel embedders), chat behaves as before.
   */
  crossChannelMemory?: CrossChannelMemory;
}

interface ChatStrings {
  title: string;
  placeholder: string;
  send: string;
  connecting: string;
  error: string;
  retry: string;
  switchToVoice: string;
  close: string;
  toolBusy: string;
  toolBusyHint: string;
  muteSounds: string;
  unmuteSounds: string;
}

/** Idle inactivity before the chat auto-completes (flushes its transcript
 *  to n8n and collapses the panel) when no explicit goodbye was detected.
 *  Reset on every message, keystroke, and tool/thinking activity. */
const IDLE_AUTOCLOSE_MS = 120_000;

const STRINGS: Record<LanguageCode, ChatStrings> = {
  he: {
    title: 'צ\'אט טקסט',
    placeholder: 'הקלידו הודעה...',
    send: 'שליחה',
    connecting: 'מתחבר...',
    error: 'שגיאת חיבור',
    retry: 'ניסיון מחדש',
    switchToVoice: 'מעבר לשיחה קולית',
    close: 'סגירה',
    toolBusy: 'מבצע פעולה...',
    toolBusyHint: 'לא ניתן להחליף ערוץ כעת',
    muteSounds: 'השתקת צלילי הצ\'אט',
    unmuteSounds: 'הפעלת צלילי הצ\'אט',
  },
  en: {
    title: 'Text chat',
    placeholder: 'Type a message…',
    send: 'Send',
    connecting: 'Connecting…',
    error: 'Connection error',
    retry: 'Retry',
    switchToVoice: 'Switch to voice',
    close: 'Close',
    toolBusy: 'Working…',
    toolBusyHint: 'Channel switching is disabled during a tool call',
    muteSounds: 'Mute chat sounds',
    unmuteSounds: 'Unmute chat sounds',
  },
  ru: {
    title: 'Текстовый чат',
    placeholder: 'Введите сообщение…',
    send: 'Отпр.',
    connecting: 'Подключение…',
    error: 'Ошибка соединения',
    retry: 'Повторить',
    switchToVoice: 'Перейти к голосу',
    close: 'Закрыть',
    toolBusy: 'Обработка…',
    toolBusyHint: 'Переключение каналов отключено во время операции',
    muteSounds: 'Отключить звуки чата',
    unmuteSounds: 'Включить звуки чата',
  },
  fr: {
    title: 'Chat texte',
    placeholder: 'Écrivez un message…',
    send: 'Envoyer',
    connecting: 'Connexion…',
    error: 'Erreur de connexion',
    retry: 'Réessayer',
    switchToVoice: 'Passer en vocal',
    close: 'Fermer',
    toolBusy: 'Traitement…',
    toolBusyHint: 'Le changement de canal est désactivé pendant un appel d\'outil',
    muteSounds: 'Couper les sons du chat',
    unmuteSounds: 'Réactiver les sons du chat',
  },
  ar: {
    title: 'الدردشة النصية',
    placeholder: 'اكتب رسالة…',
    send: 'إرسال',
    connecting: 'يتصل…',
    error: 'خطأ في الاتصال',
    retry: 'إعادة المحاولة',
    switchToVoice: 'التحويل إلى الصوت',
    close: 'إغلاق',
    toolBusy: 'جارٍ المعالجة…',
    toolBusyHint: 'تعطيل تبديل القناة أثناء استدعاء الأداة',
    muteSounds: 'كتم أصوات الدردشة',
    unmuteSounds: 'تشغيل أصوات الدردشة',
  },
};

/**
 * Minimal markdown renderer for chat bubbles.
 *
 * The chat model returns markdown-flavored text (e.g. "**KIR1** - שכונה
 * מערבית, **קריית אתא**" or numbered lists). Without rendering, the
 * literal "**" markers leak into the UI and visually break mixed
 * Hebrew/English lines. We deliberately avoid adding `react-markdown`
 * (~50 kB) to the embed bundle and instead handle the small subset the
 * model actually produces: bold, italic, inline code, and per-line list
 * markers. Newlines are preserved by the bubble's `whiteSpace: 'pre-wrap'`.
 *
 * Each line is wrapped in a span with `dir="auto"` so the BiDi
 * algorithm picks RTL when the first strong char is Hebrew/Arabic and
 * LTR otherwise, even if the line starts with a neutral list marker
 * like "1." or "- ". Embedded LTR runs (project codes like KIR1)
 * remain in their natural position inside the RTL paragraph.
 */
function renderInlineMarkdown(text: string, keyPrefix: string): React.ReactNode[] {
  // Tokenize **bold**, *italic*/_italic_, and `code`.
  // Order matters: bold (**) before italic (*) to avoid mis-matching.
  const tokens: React.ReactNode[] = [];
  const re = /(\*\*([^*\n]+)\*\*)|(`([^`\n]+)`)|(\b_([^_\n]+)_\b)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }
    const key = `${keyPrefix}-md-${i++}`;
    if (match[2] !== undefined) {
      tokens.push(<strong key={key}>{match[2]}</strong>);
    } else if (match[4] !== undefined) {
      tokens.push(
        <code
          key={key}
          style={{
            background: 'rgba(0,0,0,0.06)',
            padding: '0 4px',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.92em',
          }}
        >
          {match[4]}
        </code>,
      );
    } else if (match[6] !== undefined) {
      tokens.push(<em key={key}>{match[6]}</em>);
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }
  return tokens.length > 0 ? tokens : [text];
}

/**
 * Determine the base BiDi direction of a whole message from its first
 * strong directional character (mirrors the UAX#9 P2/P3 rule that
 * `dir="auto"` uses, but computed ONCE for the entire message rather
 * than per wrapped line). Falls back to RTL because this product is
 * Hebrew-primary, so neutral-only strings (numbers, punctuation) align
 * right like the surrounding conversation.
 */
function firstStrongDir(text: string): 'rtl' | 'ltr' {
  // RTL: Hebrew + Arabic blocks (incl. presentation forms).
  const rtl =
    /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0750-\u077F\u08A0-\u08FF\uFB1D-\uFB4F\uFB50-\uFDFF\uFE70-\uFEFF]/;
  // LTR: basic + extended Latin letters.
  const ltr = /[A-Za-z\u00C0-\u024F]/;
  for (const ch of text) {
    if (rtl.test(ch)) return 'rtl';
    if (ltr.test(ch)) return 'ltr';
  }
  return 'rtl';
}

function renderChatMarkdown(text: string, keyPrefix: string): React.ReactNode {
  // Split on newlines so each line gets its own dir="auto" context.
  // pre-wrap on the parent preserves the actual line breaks visually.
  const lines = text.split('\n');
  // Compute ONE base direction for the whole message (first strong char)
  // and apply it to every line, instead of letting each wrapped line
  // auto-detect. This keeps embedded LTR runs (e.g. "PRP", "ד"ר",
  // project codes) in their correct logical position inside a stable
  // RTL paragraph instead of flipping individual lines to LTR.
  const baseDir = firstStrongDir(text);
  return lines.map((line, idx) => (
    <span
      key={`${keyPrefix}-l-${idx}`}
      dir={baseDir}
      style={{ display: 'block', unicodeBidi: 'isolate' }}
    >
      {renderInlineMarkdown(line, `${keyPrefix}-l-${idx}`)}
      {idx < lines.length - 1 ? '\n' : null}
    </span>
  ));
}

export const ChatModePanel: React.FC<ChatModePanelProps> = ({
  isMounted,
  config,
  voiceAvailable,
  onSwitchToVoice,
  onClose,
  crossChannelMemory,
}) => {
  const language = (config.language ?? DEFAULT_LANGUAGE) as LanguageCode;
  const t = STRINGS[language] ?? STRINGS.en;
  const primary = config.primaryColor || '#3b82f6';
  const isMobile = useIsMobile();

  const {
    status,
    messages,
    errorMessage,
    isToolCallInFlight,
    isAssistantThinking,
    sendMessage,
    sendSystemNote,
    reconnect,
    completeSession,
    flushLead,
    customerId,
    lastToolEvents,
    toolEventsVersion,
    reportContactInfo,
    reportScheduledMeeting,
  } = useChatSession({
    isMounted,
    config,
    crossChannelMemory,
  });

  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  // ----- Audio feedback (typing while tool runs, ding on new reply) -----
  // Mute is chat-only and session-scoped; voice mode keeps using
  // typingSound directly and is unaffected.
  const [soundsMuted, setSoundsMuted] = useState(() => chatSounds.isMuted());
  useEffect(() => {
    chatSounds.setMuted(soundsMuted);
  }, [soundsMuted]);

  // Play the typing sound while a tool call is in flight (RAG search,
  // calendly lookup, etc.). Mirrors the voice channel's behavior of
  // playing typingSound during function execution.
  useEffect(() => {
    if (isToolCallInFlight) {
      chatSounds.startTyping('typing');
    } else {
      chatSounds.stopTyping();
    }
    return () => chatSounds.stopTyping();
  }, [isToolCallInFlight]);

  // Gentle "ding" on each new assistant message. We track the id of the
  // last message we dinged for so streaming deltas and re-renders don't
  // re-trigger the chime. The very first render (greeting) is skipped
  // so opening the panel doesn't beep at the user.
  const lastDingedIdRef = useRef<string | null>(null);
  const dingPrimedRef = useRef(false);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    // Skip the first assistant message we ever see (initial greeting on
    // mount). After that, every newly-arrived assistant message dings.
    if (!dingPrimedRef.current) {
      dingPrimedRef.current = true;
      lastDingedIdRef.current = last.id;
      return;
    }
    if (last.id !== lastDingedIdRef.current && !isAssistantThinking) {
      lastDingedIdRef.current = last.id;
      chatSounds.ding();
    }
  }, [messages, isAssistantThinking]);

  // ----- Scheduling flow state (mirrors VoiceBotPanel) -----
  const [scheduledMeeting, setScheduledMeeting] =
    useState<ScheduledMeeting | null>(null);
  const [showCalendlyEmbed, setShowCalendlyEmbed] = useState(false);
  const [showCalendlyOverlay, setShowCalendlyOverlay] = useState(false);
  /** Calendly-enabled chat collects the visitor's details in the same form
   *  voice uses, never in the chat itself. */
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [pendingBookArgs, setPendingBookArgs] = useState<Record<string, unknown> | null>(null);
  /** Guards against re-handling the SAME tool-events bump. */
  const lastHandledVersionRef = useRef(0);
  /** Latest contact details collected inline in the chat, for the
   *  version-guarded watcher effect and the n8n transcript flush. */
  const contactInfoRef = useRef<ContactInfo | null>(null);
  /** Guards against double-firing the goodbye system note when Calendly
   *  posts both confirmation and a follow-up event. */
  const goodbyeSentRef = useRef(false);

  // Mobile overlay coordinator — reuses the voice channel's hook so the
  // mobile open/close timing stays consistent across both channels.
  useCalendlyOverlaySync(
    isMobile,
    showCalendlyEmbed,
    /* bookingConfirmed */ false,
    showCalendlyOverlay,
    setShowCalendlyOverlay,
  );

  // Auto-scroll on new messages or deltas.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isAssistantThinking]);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (isAssistantThinking || isToolCallInFlight) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.internal) return;
    if (!isTerminalGoodbye(last.text)) return;

    const timer = window.setTimeout(() => {
      completeSession();
      onClose();
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [messages, isAssistantThinking, isToolCallInFlight, completeSession, onClose]);

  // ----- Idle auto-complete safety net -----
  // Catches the "conversation ended but the bot never said an explicit
  // goodbye, and the visitor walked away with the window still open" case.
  // Without it the transcript would only ever flush on tab-close (and the
  // mobile beacon is unreliable), so n8n might never process the chat.
  // After IDLE_AUTOCLOSE_MS of total inactivity — no new messages, no typing,
  // no tool/thinking activity — AND only once the visitor has actually
  // engaged, we flush the transcript ('completed') and collapse the panel.
  // The timer resets on every dependency change below (new message, keypress,
  // bot activity), so it only fires when the chat is genuinely quiet.
  useEffect(() => {
    if (status !== 'ready') return;
    if (isAssistantThinking || isToolCallInFlight) return;
    if (showCalendlyEmbed) return; // user is mid-booking — never close on them
    if (showContactPopup) return; // typing in the details form doesn't touch chat state
    // Require at least one real visitor turn so we don't close an untouched
    // panel (greeting-only) or a voice→chat handoff the user hasn't used yet.
    const hasUserTurn = messages.some((m) => m.role === 'user' && !m.internal);
    if (!hasUserTurn) return;

    const timer = window.setTimeout(() => {
      completeSession();
      onClose();
    }, IDLE_AUTOCLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [
    status,
    messages,
    isAssistantThinking,
    isToolCallInFlight,
    showCalendlyEmbed,
    showContactPopup,
    input,
    completeSession,
    onClose,
  ]);

  // ----- book_meeting tool-event watcher -----
  // The chat dispatcher returns `needs_contact_info: true` instead of
  // booking — see api/_lib/chat-tool-dispatcher.dispatchBookMeeting.
  // We react by opening the SchedulingContactPopup (or re-using the
  // already-collected contactInfo for follow-up bookings).
  useEffect(() => {
    if (toolEventsVersion === 0) return;
    if (toolEventsVersion === lastHandledVersionRef.current) return;
    lastHandledVersionRef.current = toolEventsVersion;

    const bookEvent = lastToolEvents.find(
      (e: ChatToolEvent) => e.name === 'book_meeting',
    );
    if (!bookEvent) return;
    // The dispatcher inlines the slot args in the result so the client
    // doesn't need to round-trip through args (which may be a JSON
    // string the server already parsed). Prefer result, fall back to args.
    const result = bookEvent.result as Record<string, unknown> | undefined;
    const argsObj = bookEvent.args as Record<string, unknown> | undefined;
    if (!result?.needs_contact_info) return; // server flow indicated no popup needed

    const slotStart =
      (result?.slotStart as string | undefined) ||
      (argsObj?.slotStart as string | undefined) ||
      null;
    const customerName =
      (result?.customerName as string | undefined) ||
      (argsObj?.customerName as string | undefined) ||
      null;
    const meetingType =
      (result?.meetingType as string | undefined) ||
      (argsObj?.meetingType as string | undefined) ||
      null;
    const location =
      (result?.location as string | undefined) ||
      (argsObj?.location as string | undefined) ||
      null;
    // Calendly OFF: the model collects these in the conversation and passes
    // them as book_meeting args; the dispatcher echoes them back in the
    // result. (Calendly ON uses the details form instead — see below.)
    const collectedEmail =
      (result?.customerEmail as string | undefined) ||
      (argsObj?.customerEmail as string | undefined) ||
      null;
    const collectedPhone =
      (result?.customerPhone as string | undefined) ||
      (argsObj?.customerPhone as string | undefined) ||
      null;
    // Server tells the client whether the popup should chain into Calendly
    // (true) or just capture the lead and let /api/chat-transcript forward
    // it to n8n at session close (false). Default true preserves the
    // existing behavior for older server builds.
    const calendlyEnabled =
      typeof result?.calendly_enabled === 'boolean'
        ? (result.calendly_enabled as boolean)
        : true;

    // slotStart MAY be null — voice supports browse-mode `book_meeting`
    // (bot opens calendar for the visitor to pick a time). `/api/calendly-book`
    // accepts null slotStart and returns a generic Calendly link the iframe
    // shows for free browsing. Do NOT short-circuit on null — that would
    // break the chat-mode browse flow that already works in voice.
    const newBookArgs = { slotStart, customerName, meetingType, location, calendlyEnabled };
    // Reset goodbye guard for this new booking attempt.
    goodbyeSentRef.current = false;

    // Is this a SYNTHETIC safeguard event (server attached it because the
    // bot's reply *sounded* like it promised a callback / opened a form,
    // even though it never actually called book_meeting)?
    const isSynthetic = result?.synthetic === true;

    // Calendly on → the details form owns collection (name + email + phone),
    // exactly like the voice channel. Reuse a lead already submitted in this
    // session so a second booking doesn't re-ask. Synthetic events count here:
    // the server emits them precisely when the bot promised/asked for details
    // without calling the tool, and opening the form is the recovery.
    if (calendlyEnabled) {
      if (showCalendlyEmbed || scheduledMeeting) return; // already past this step
      const onFile = contactInfoRef.current;
      if (onFile?.email && onFile?.phone) {
        if (isSynthetic) return; // don't re-book off a stray phrase
        void proceedWithBooking(newBookArgs, onFile);
      } else {
        setPendingBookArgs(newBookArgs);
        setShowContactPopup(true);
      }
      return;
    }

    // Is this a SYNTHETIC safeguard event (server attached it because the
    // bot's reply *sounded* like it promised a callback / opened a form,
    // even though it never actually called book_meeting)? It never carries
    // collected contact fields, so we never act on it here: if the lead is
    // already on file the booking has already happened, and otherwise the
    // model is nudged server-side to finish collecting. Ignoring it also
    // prevents the post-lead goodbye from re-tripping the safeguard into an
    // infinite goodbye loop.
    if (isSynthetic) {
      return;
    }

    // Merge freshly-collected inline details with anything already on file.
    const existingContact = contactInfoRef.current;
    const email = collectedEmail || existingContact?.email || null;
    const phone = collectedPhone || existingContact?.phone || null;
    const name = customerName || existingContact?.name || undefined;

    // Persist whatever the model has collected so far — even a PARTIAL lead
    // (e.g. name + phone but no email yet). Previously contactInfo was only
    // ever set inside proceedWithBooking, which requires BOTH email AND
    // phone, so a half-finished lead reached n8n only as free text inside
    // the transcript (which the AI summary parses) while the STRUCTURED
    // contactInfo field — and therefore the email's top name/email/phone
    // table — stayed empty. The server keys the n8n contact block off
    // email OR phone, so reporting a phone-only lead is enough to fill it.
    if (name || email || phone) {
      const partialContact: ContactInfo = {
        name: name || undefined,
        email: email || '',
        phone: phone || '',
      };
      contactInfoRef.current = partialContact;
      reportContactInfo(partialContact);
    }

    // Calendly OFF (inline lead capture): proceed to the callback only once
    // we actually have email AND phone (name is best-effort). If the model
    // called book_meeting before collecting everything, the dispatcher
    // already returned a note nudging it to ask for the missing field(s),
    // so we simply wait.
    if (email && phone) {
      void proceedWithBooking(newBookArgs, { email, phone, name });
    }
  }, [toolEventsVersion, lastToolEvents]);

  // ----- Calendly iframe postMessage listener -----
  // Mirrors useCalendlyMessageListener (voice) but tailored to chat:
  // we don't need to feed a datachannel — we just send a hidden
  // [SYSTEM: …] note via sendSystemNote so the bot acknowledges and
  // says goodbye in the visitor's language.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onMessage = (event: MessageEvent) => {
      const isCalendlyOrigin =
        event.origin === 'https://calendly.com' ||
        event.origin.endsWith('.calendly.com');
      if (!isCalendlyOrigin) return;
      if (event.data?.event !== 'calendly.event_scheduled') return;
      if (goodbyeSentRef.current) return;
      goodbyeSentRef.current = true;
      setShowCalendlyEmbed(false);
      const langMessages =
        CALENDLY_SYSTEM_MESSAGES[language] ||
        CALENDLY_SYSTEM_MESSAGES[DEFAULT_LANGUAGE];
      sendSystemNote(langMessages.bookingConfirmedGoodbye);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [language, sendSystemNote]);

  // ----- Popup submit → either POST /api/calendly-book + open iframe (Calendly on)
  //                     or save lead + send goodbye (Calendly off, lead flushes to n8n via transcript). -----
  // Core booking action shared by the popup submit and the "reuse already
  // collected contact info" fast path. `info` MUST already contain
  // email + phone. Defined as a plain function so the version-guarded
  // watcher effect can call it without adding it to the dep array.
  const proceedWithBooking = async (
    bookArgs: Record<string, unknown>,
    info: ContactInfo,
  ) => {
    contactInfoRef.current = info;
    // Forward the structured lead to the session so the n8n transcript
    // flush carries contact.{name,email,phone} (parity with the old
    // details-form payload), not just free-text in the transcript.
    reportContactInfo(info);
    const resolvedName = info.name || '';
    const email = info.email;
    const phone = info.phone;

    const popupCalendlyEnabled =
      (bookArgs.calendlyEnabled as boolean | undefined) !== false;

    // No Calendly configured: don't POST to /api/calendly-book. Record the
    // lead so it rides out to n8n with the transcript flush at session
    // close, then tell the bot to confirm and wrap up in the visitor's
    // language.
    if (!popupCalendlyEnabled) {
      const callbackMeeting: ScheduledMeeting = {
        datetime: '',
        formattedTime: '',
        customerName: resolvedName,
        customerEmail: email,
        customerPhone: phone,
        bookingUrl: '',
        source: 'callback',
      };
      setScheduledMeeting(callbackMeeting);
      reportScheduledMeeting(callbackMeeting);
      // Business-critical lead is on file — push it to n8n now (non-closing)
      // so it isn't hostage to how the window eventually closes.
      flushLead();
      sendSystemNote(
        '[SYSTEM: You now have the visitor\'s contact details (name, email, phone). A representative will get back to them — there is no on-screen calendar in this account. In the visitor\'s language, briefly confirm that we received the details and a team member will get back to them soon, then say goodbye. Keep it to 1–2 short sentences.]',
      );
      return;
    }

    const bookingPayload = {
      slotStart: bookArgs.slotStart,
      customerName: resolvedName,
      customerEmail: email,
      customerPhone: phone,
      meetingType: bookArgs.meetingType,
      location: bookArgs.location,
      customerId, // From useChatSession — resolved server-side from public key
    };

    try {
      const resp = await postCalendlyBook(bookingPayload);
      if (!resp.ok) {
        console.error('[chat-panel] calendly-book HTTP error:', resp.status);
        sendSystemNote(
          '[SYSTEM: The calendar could not be opened due to a system error. Apologize briefly in the visitor\'s language and offer to take their details so a representative can call them back.]',
        );
        return;
      }
      const data = (await resp.json()) as {
        success?: boolean;
        bookingUrl?: string;
        eventDetails?: { formattedTime?: string };
      };
      if (!data.success || !data.bookingUrl) {
        sendSystemNote(
          '[SYSTEM: The calendar could not be opened. Apologize briefly in the visitor\'s language and offer to take their details so a representative can call them back.]',
        );
        return;
      }
      const meeting: ScheduledMeeting = {
        datetime: (bookArgs.slotStart as string) || '',
        formattedTime: data.eventDetails?.formattedTime || '',
        customerName: resolvedName,
        customerEmail: email,
        customerPhone: phone,
        bookingUrl: data.bookingUrl,
        source: 'calendly',
      };
      setScheduledMeeting(meeting);
      reportScheduledMeeting(meeting);
      // Lead + booking captured — flush to n8n now (non-closing); the chat
      // continues into the Calendly iframe.
      flushLead();
      setShowCalendlyEmbed(true);
      // Tell the bot the calendar is open — same localized prompt voice uses.
      const langMessages =
        CALENDLY_SYSTEM_MESSAGES[language] ||
        CALENDLY_SYSTEM_MESSAGES[DEFAULT_LANGUAGE];
      sendSystemNote(langMessages.bookingFormOpened);
    } catch (err) {
      console.error('[chat-panel] calendly-book threw', err);
      sendSystemNote(
        '[SYSTEM: The calendar could not be opened due to a network error. Apologize briefly in the visitor\'s language and offer to take their details so a representative can call them back.]',
      );
    }
  };

  // Filter out internal [SYSTEM: …] messages from the visible bubble list.
  const visibleMessages = useMemo(
    () => messages.filter((m) => !m.internal && (m.role === 'user' || m.role === 'assistant')),
    [messages],
  );

  const canSend = status === 'ready' && input.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter sends. The composer is a single-line <input> so there is no
    // multi-line behavior — Shift+Enter / Alt+Enter just do nothing
    // extra (modifier keys won't trigger send either to avoid double-fire).
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <AnimatePresence>
        {showContactPopup && (
          <SchedulingContactPopup
            key="chat-scheduling-popup"
            primaryColor={primary}
            defaultName={(pendingBookArgs?.customerName as string | undefined) || undefined}
            mode="calendly"
            onSubmit={(email, phone, formName) => {
              const info: ContactInfo = {
                name: formName || (pendingBookArgs?.customerName as string | undefined) || undefined,
                email,
                phone,
              };
              setShowContactPopup(false);
              // Facebook Pixel: contact details submitted to receive the
              // scheduling link — the campaign's Lead conversion. No-op unless
              // the customer enabled a pixel. Chat channel.
              trackFacebookLead();
              void proceedWithBooking(pendingBookArgs || {}, info);
              setPendingBookArgs(null);
            }}
            onDismiss={() => {
              setShowContactPopup(false);
              setPendingBookArgs(null);
              sendSystemNote(
                '[SYSTEM: The visitor closed the details form without submitting it, so no calendar opened and no details were received. In the visitor\'s language, briefly say that is fine and that you can re-open the form whenever they are ready. Do NOT ask for their name, email or phone in the chat.]',
              );
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        <CalendlyPanel
          key="chat-calendly-panel"
          isMobile={isMobile}
          showCalendlyEmbed={showCalendlyEmbed}
          showCalendlyOverlay={showCalendlyOverlay}
          scheduledMeeting={scheduledMeeting}
          primaryColor={primary}
          panelWidth={config.panelWidth}
          onClose={() => setShowCalendlyEmbed(false)}
        />
      </AnimatePresence>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: '#ffffff',
          boxSizing: 'border-box',
          // Dim the chat panel slightly when the mobile Calendly overlay
          // is open, matching the voice channel's UX.
          opacity: isMobile && showCalendlyOverlay ? 0.3 : 1,
          transition: 'opacity 200ms ease',
        }}
      >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 15, color: '#111827' }}>{t.title}</strong>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setSoundsMuted((m) => !m)}
            aria-label={soundsMuted ? t.unmuteSounds : t.muteSounds}
            title={soundsMuted ? t.unmuteSounds : t.muteSounds}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 6,
              border: '1px solid #d1d5db',
              background: '#ffffff',
              color: soundsMuted ? '#9ca3af' : primary,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {soundsMuted ? (
              // Speaker with X (muted)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              // Speaker with waves (audible)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
          {voiceAvailable && (
            <button
              type="button"
              onClick={onSwitchToVoice}
              disabled={isToolCallInFlight}
              title={isToolCallInFlight ? t.toolBusyHint : t.switchToVoice}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: `1px solid ${primary}`,
                background: '#ffffff',
                color: primary,
                fontSize: 12,
                cursor: isToolCallInFlight ? 'not-allowed' : 'pointer',
                opacity: isToolCallInFlight ? 0.5 : 1,
              }}
            >
              {t.switchToVoice}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              // Flush the transcript to n8n BEFORE the panel closes.
              // Previously the close button only called onClose(), so a
              // visitor who closed the window without the bot saying
              // goodbye never had their conversation forwarded. completeSession
              // is idempotent (transcriptSentRef guards double-send), so the
              // later unmount/pagehide flush becomes a safe no-op.
              completeSession();
              onClose();
            }}
            aria-label={t.close}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #d1d5db',
              background: '#ffffff',
              color: '#374151',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {t.close}
          </button>
        </div>
      </div>

      {/* Status / error banner */}
      {status === 'connecting' && (
        <div style={{ padding: '8px 16px', fontSize: 12, color: '#6b7280' }}>{t.connecting}</div>
      )}
      {status === 'error' && (
        <div
          style={{
            padding: '8px 16px',
            fontSize: 12,
            color: '#b91c1c',
            background: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span>
            {t.error}
            {errorMessage ? `: ${errorMessage}` : ''}
          </span>
          <button
            type="button"
            onClick={reconnect}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: `1px solid ${primary}`,
              background: primary,
              color: '#ffffff',
              fontSize: 12,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {t.retry}
          </button>
        </div>
      )}
      {isToolCallInFlight && (
        <div style={{ padding: '6px 16px', fontSize: 11, color: '#6b7280' }}>{t.toolBusy}</div>
      )}

      {/* Message list */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {visibleMessages.map((m) => {
          const isUser = m.role === 'user';
          const baseDir = firstStrongDir(m.text);
          return (
            <div
              key={m.id}
              // Base BiDi direction is computed ONCE for the whole message
              // (first strong char) and applied to the bubble + every line
              // inside `renderChatMarkdown`. This keeps embedded LTR runs
              // (English words like "PRP", project codes like KIR1) in
              // their correct logical position inside a stable RTL
              // paragraph, instead of letting individual wrapped lines
              // flip direction when they happen to start with a Latin word.
              dir={baseDir}
              style={{
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: 12,
                background: isUser ? primary : '#f3f4f6',
                color: isUser ? '#ffffff' : '#111827',
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: baseDir === 'rtl' ? 'right' : 'left',
              }}
            >
              {renderChatMarkdown(m.text, m.id)}
              {m.inProgress && <span style={{ opacity: 0.5 }}>▍</span>}
            </div>
          );
        })}
        {isAssistantThinking && (
          <div
            aria-live="polite"
            aria-label={t.connecting}
            style={{
              alignSelf: 'flex-start',
              padding: '10px 14px',
              borderRadius: 12,
              background: '#f3f4f6',
              color: '#6b7280',
              fontSize: 14,
              display: 'inline-flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <span style={{ animation: 'iland-chat-dot 1.4s infinite', animationDelay: '0s' }}>•</span>
            <span style={{ animation: 'iland-chat-dot 1.4s infinite', animationDelay: '0.2s' }}>•</span>
            <span style={{ animation: 'iland-chat-dot 1.4s infinite', animationDelay: '0.4s' }}>•</span>
            <style>{`@keyframes iland-chat-dot { 0%, 80%, 100% { opacity: 0.2; } 40% { opacity: 1; } }`}</style>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          padding: 12,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: 8,
        }}
      >
        {/* Scoped composer styles. We use a real <style> block (not inline)
         *  because placeholder + autofill + selection states are only
         *  reachable via CSS pseudo-classes. The .iland-chat-input class
         *  is unique enough that customer-site CSS shouldn't collide. */}
        <style>{`
          .iland-chat-input {
            color: #111827 !important;
            -webkit-text-fill-color: #111827 !important;
            caret-color: ${primary};
          }
          .iland-chat-input::placeholder {
            color: #9ca3af !important;
            opacity: 1 !important;
          }
          .iland-chat-input:disabled {
            color: #6b7280 !important;
            -webkit-text-fill-color: #6b7280 !important;
          }
        `}</style>
        <input
          className="iland-chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.placeholder}
          disabled={status !== 'ready'}
          // dir="auto" so user typing Hebrew flows RTL and English flows
          // LTR within the same input — mirrors the bubble behavior.
          dir="auto"
          style={{
            flex: 1,
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            // 16px prevents iOS Safari from zooming the page on focus.
            // Previous 14px caused both a visual zoom AND made the
            // already-low-contrast text harder to read on small phones.
            fontSize: 16,
            lineHeight: 1.4,
            outline: 'none',
            background: status === 'ready' ? '#ffffff' : '#f9fafb',
            // Explicit color here too as a belt-and-braces for embeds
            // where the customer site sets a global `input { color: ... }`.
            color: '#111827',
            WebkitTextFillColor: '#111827',
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            border: 'none',
            background: canSend ? primary : '#d1d5db',
            color: '#ffffff',
            fontWeight: 600,
            cursor: canSend ? 'pointer' : 'not-allowed',
          }}
        >
          {t.send}
        </button>
      </div>
    </div>
    </>
  );
};

export default ChatModePanel;
